import { Button, Card, Collapse, Form, Input, Select, Switch } from "antd";
import { getDeliveriesAdmin, getListCompany, getListDomainAdmin } from "api";
import DashboardHeader from "components/dashboard-header/DashboardHeader";
import DatePicker from "components/datepicker/DatePicker";
import { LoadFailed } from "components/empty/EmptyComponent";
import { ICCloseOutlined, ICSearchOutlined, ICUpOutlined } from "components/list/ListIcon";
import TableDeliveries from "components/table/TableDeliveries";
import { endOfDay, format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setListAllCompany } from "redux/slices/admin/listAllCompany";
import { setListDomainAdmin } from "redux/slices/admin/listDomainAdmin";
import { setDeliveriesAdmin } from "redux/slices/admin/deliveriesAdmin";
import useCursorPagination from "utils/hooks/useCursorPagination";
import useDocumentTitle from "utils/useDocumentTitle";
import "./deliveries.less";

const ExtraHeader = ({ filter, setFilter, onSearchDomain, name, listDomains }) => {
  const dispatch = useDispatch();
  const listDomainAdmin = useSelector(({ listDomainAdmin: domainList }) => domainList.value);
  const listAllCompany = useSelector(({ listAllCompany }) => listAllCompany.value);

  const getDataDomain = useCallback(async () => {
    const { data } = await getListDomainAdmin();
    dispatch(setListDomainAdmin(data));
  }, [dispatch]);

  const getDataCompany = useCallback(async () => {
    const { data } = await getListCompany();
    if (data && Object.keys(data).length) dispatch(setListAllCompany(data));
  }, [dispatch]);

  useEffect(() => {
    if (!listDomainAdmin) getDataDomain();
  }, [getDataDomain, listDomainAdmin]);

  useEffect(() => {
    if (!listAllCompany) getDataCompany();
  }, [getDataCompany, listAllCompany]);

  const handleChangeDomain = async (domain) => {
    setFilter({ ...filter, domain, page: 1 });
  };

  const handleChangeStatus = async (status) => {
    setFilter({ ...filter, status, page: 1 });
  };

  const handleChangeCompany = (company_uuid) => {
    const next = { ...filter, page: 1 };
    if (company_uuid) next.company_uuid = company_uuid;
    else delete next.company_uuid;
    setFilter(next);
  };

  const handleChangeAllHops = (checked) => {
    const next = { ...filter, page: 1 };
    if (checked) next.all_hops = true;
    else delete next.all_hops;
    setFilter(next);
  };

  const showDomain = listDomains.length > 0 ? listDomains : listDomainAdmin;

  return (
    <div className="delivery-filter-bar flex justify-between items-center flex-wrap gap-y-3">
      <span className="title-log-email">Filter</span>
      <div className="delivery-filter-controls flex gap-x-6 gap-y-3 items-center flex-wrap">
        <div className="delivery-filter-control flex gap-x-2 items-center" onClick={(e) => e.stopPropagation()}>
          <span className="hidden-xs">Semua Hop:</span>
          <Switch size="small" checked={!!filter.all_hops} onChange={handleChangeAllHops} />
        </div>
        <div className="delivery-filter-control flex gap-x-1 items-center">
          <span className="hidden-xs">Pelanggan:</span>
          <Select
            showSearch
            allowClear
            optionFilterProp="children"
            onClick={(e) => e.stopPropagation()}
            onChange={handleChangeCompany}
            placeholder={<span className="text-primary">Semua Pelanggan</span>}
            className="company-list"
            filterOption={(input, option) =>
              (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
            }>
            {listAllCompany?.length &&
              listAllCompany.map((company) => (
                <Select.Option key={company.company_uuid} value={company.company_uuid}>
                  {company.company_name}
                </Select.Option>
              ))}
          </Select>
        </div>
        <div className="delivery-filter-control flex gap-x-1 items-center">
          <span className="hidden-xs">Domain:</span>
          <Select
            name="domain"
            onClick={(e) => e.stopPropagation()}
            onChange={handleChangeDomain}
            placeholder={<span className="text-primary">Semua Domain</span>}
            className="domain-list"
            dropdownRender={(menu) => (
              <>
                <Input
                  placeholder="Ketik Domain.."
                  value={name}
                  onChange={(e) => onSearchDomain(e)}
                />
                {menu}
              </>
            )}>
            <Select.Option value="">Semua Domain</Select.Option>
            {showDomain?.length &&
              showDomain.map(({ name }, index) => (
                <Select.Option key={name + index} value={name}>
                  {name}
                </Select.Option>
              ))}
          </Select>
        </div>
        <div className="delivery-filter-control flex gap-x-1 items-center">
          <span className="hidden-xs">Status:</span>
          <Select
            name="status"
            placeholder={<span className="text-primary">Semua Status</span>}
            className="status-list"
            popupClassName="filter-dropdown"
            onChange={handleChangeStatus}
            onClick={(e) => e.stopPropagation()}>
            <Select.Option value="">Semua Status</Select.Option>
            <Select.Option value="in_queue">In Queue</Select.Option>
            <Select.Option value="deferred">Deferred</Select.Option>
            <Select.Option value="sent">Sent</Select.Option>
            <Select.Option value="bounced">Bounced</Select.Option>
            <Select.Option value="expired">Expired</Select.Option>
          </Select>
        </div>
      </div>
    </div>
  );
};

const HeaderPanel = () => {
  const [click, setClick] = useState(false);

  return (
    <div className="flex items-center w-full">
      <span className="text-success" onClick={() => setClick((prev) => !prev)}>
        Advanced Filter
      </span>
      <ICUpOutlined className="ml-1 text-success" rotate={click ? 0 : 180} />
    </div>
  );
};

const RenderTable = ({ filter, setFilter, loading, getData, data, pagination, onRefresh }) => {
  if (data && data.tableDeliveriesAdmin)
    return (
      <TableDeliveries
        filter={filter}
        setFilter={setFilter}
        loading={loading}
        hitApi={getData}
        onRefresh={onRefresh}
        pageIndex={pagination.pageIndex}
        canPrev={pagination.canPrev}
        onPrev={pagination.goPrev}
        onNext={pagination.goNext}
        emptyState={{
          title: data.success ? "Data tidak ditemukan" : "Data belum tersedia",
          message: data.success
            ? "Data pengiriman yang anda cari tidak ditemukan, pastikan tidak ada salah ketik pada penulisan anda"
            : `Data pengiriman akan muncul setelah client melakukan pengiriman email menggunakan akun ${process.env.REACT_APP_NAME}.`,
        }}
      />
    );

  if (data)
    return (
      <Card>
        <LoadFailed />
      </Card>
    );

  return <Card loading />;
};

const CollapseFilter = ({ filter, setFilter, getData }) => {
  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState(false);
  const [endDate, setEndDate] = useState(false);
  const [listDomains, setListDomains] = useState([]);
  const [name, setName] = useState("");
  const listDomainAdmin = useSelector(({ listDomainAdmin: domainList }) => domainList.value);

  const resetfilter = () => {
    form.resetFields();
    delete filter.datetime;
    delete filter.to_datetime;
    let filtered = {
      ...filter,
      page: 1,
      sender: "",
      recipient: "",
      subject: "",
    };
    setFilter(filtered);
    getData();
  };

  const onChangeDatetime = (values) => {
    if (values && values.length > 0) {
      const startDate = format(new Date(values[0]), "yyyy-MM-dd");
      const endDate = format(new Date(values[1]), "yyyy-MM-dd");
      setStartDate(`${startDate} 00:00:00`);
      setEndDate(`${endDate} 23:59:59`);
    } else {
      setStartDate(false);
      setEndDate(false);
    }
  };

  const handleAdvanceFilter = async (values) => {
    const { sender, recipient, subject } = values;
    delete filter.datetime;
    delete filter.to_datetime;
    let filtered = { ...filter, sender, recipient, subject, page: 1 };
    if (startDate && endDate)
      filtered = {
        ...filter,
        page: 1,
        sender,
        recipient,
        subject,
        datetime: startDate,
        to_datetime: endDate,
      };
    setFilter(filtered);
  };

  const onSearchDomain = ({ target: { value } }) => {
    setName(value);
    if (value) {
      const filtered = listDomainAdmin.filter((domain) =>
        domain.name.toLowerCase().includes(value.toLowerCase())
      );
      setListDomains(filtered);
    } else setListDomains([]);
  };

  const disabledDate = (current) => {
    return current && current > endOfDay(new Date());
  };

  return (
    <Collapse defaultActiveKey={["0"]} ghost className="log-collapse">
      <Collapse.Panel
        header={<HeaderPanel />}
        key="1"
        extra={
          <ExtraHeader
            filter={filter}
            setFilter={setFilter}
            onSearchDomain={onSearchDomain}
            listDomains={listDomains}
            name={name}
          />
        }
        showArrow={false}
        collapsible="header"
        className="header-log-email">
        <Form
          layout="inline"
          className="formFilter"
          form={form}
          onFinish={handleAdvanceFilter}
          initialValues={{ sender: "", recipient: "", subject: "", datetime: "" }}>
          <div>
            <Form.Item label="Sender" name="sender" className="form-sender">
              <Input placeholder="sender@domain.tld" className="flex" />
            </Form.Item>
            <Form.Item label="Recipient" name="recipient" className="form-recipient">
              <Input placeholder="recipient@domain.tld" className="flex" />
            </Form.Item>
            <Form.Item label="Subject" name="subject" className="form-subject">
              <Input placeholder="Cari subject email" className="flex" />
            </Form.Item>
            <Form.Item name="datetime" className="form-daterange">
              <DatePicker.RangePicker
                className="w-full"
                disabledDate={disabledDate}
                onChange={onChangeDatetime}
                format="yyyy-MM-dd"
              />
            </Form.Item>
          </div>
          <div className="button-advance-filter">
            <Button onClick={resetfilter} icon={<ICCloseOutlined />}>
              Reset
            </Button>
            <Button type="primary" icon={<ICSearchOutlined />} htmlType="submit">
              Cari
            </Button>
          </div>
        </Form>
      </Collapse.Panel>
    </Collapse>
  );
};

const Deliveries = () => {
  useDocumentTitle("Log Email v2");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    domain: "",
    status: "",
    sender: "",
    recipient: "",
    subject: "",
    page: 1,
    limit: "10",
  });
  const dispatch = useDispatch();
  const deliveriesAdmin = useSelector(({ deliveriesAdmin }) => deliveriesAdmin.value);

  // Cursor pagination resets whenever the active filter set changes, or when the
  // user hits Refresh (bumping refreshTick) — "load the latest" from page 1.
  const [refreshTick, setRefreshTick] = useState(0);
  const { cursor, pageIndex, canPrev, goNext, goPrev } = useCursorPagination(
    `${JSON.stringify(filter)}#${refreshTick}`
  );

  const hitApiAdmin = useCallback(async () => {
    setLoading(true);
    const { data, message, success } = await getDeliveriesAdmin({ ...filter, cursor });
    dispatch(setDeliveriesAdmin({ tableDeliveriesAdmin: data, message, success }));
    setLoading(false);
  }, [filter, cursor, dispatch]);

  useEffect(() => {
    if (filter) hitApiAdmin();
    // refreshTick is a manual-refresh trigger: it re-runs the fetch even when the
    // filter/cursor are unchanged (e.g. hitting Refresh while already on page 1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hitApiAdmin, filter, refreshTick]);

  const handleRefresh = () => setRefreshTick((tick) => tick + 1);

  return (
    <div className="log-admin">
      <DashboardHeader title="Log Email Terkirim (v2)" />
      <div className="px-6 py-4">
        <div className="flex flex-col gap-y-6">
          <CollapseFilter filter={filter} setFilter={setFilter} getData={hitApiAdmin} />
          <RenderTable
            data={deliveriesAdmin}
            filter={filter}
            setFilter={setFilter}
            getData={hitApiAdmin}
            loading={loading}
            onRefresh={handleRefresh}
            pagination={{ pageIndex, canPrev, goPrev, goNext }}
          />
        </div>
      </div>
    </div>
  );
};

export default Deliveries;
